import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Typography,
  Spin,
  Table,
  Image,
  Row,
  Col,
  Divider,
  message,
  Button,
  InputNumber,
  Popconfirm,
  Modal,
  Input,
  List,
  Tag,
  Empty,
  Space,
} from "antd";
import axios from "axios";
import {
  CaretLeftOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useMediaQuery } from "react-responsive";

const { Title, Text } = Typography;

const EstimateDetail = () => {
  const { EstimateID } = useParams();
  const navigate = useNavigate();
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [draftQty, setDraftQty] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  useEffect(() => {
    checkUserActiveStatus();
    fetchEstimateDetail();
    // eslint-disable-next-line
  }, [EstimateID]);

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: "Bearer " + localStorage.getItem("token"),
  });

  const checkUserActiveStatus = async () => {
    const tokenString = localStorage.getItem("token");

    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL + "api/UserAccount/GetUserAccountDetail",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
      };

      axios
        .request(config)
        .then((response) => {
          if (
            response.data.IsActive === false ||
            response.data.IsDeleted === true
          ) {
            alert("Your session has expired. Please log in again.");

            localStorage.removeItem("token");
            localStorage.removeItem("userName");
            window.location.reload();
          }
        })
        .catch((error) => {});
    } catch (error) {}
  };

  const fetchEstimateDetail = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        global.config.APIURL.BaseURL +
          "api/Estimate/GetEstimateDetailByID?EstimateID=" +
          EstimateID,
        { headers: authHeaders() }
      );
      setEstimate(response.data || null);
    } catch (error) {
      message.error("Failed to fetch estimate detail.");
      setEstimate(null);
    }
    setLoading(false);
  };

  // Helper for Indian number format with 2 decimals
  const formatINR = (value) =>
    value !== undefined && value !== null
      ? value.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";

  // Helper for date format "dd-MM-yyyy"
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const clearDraft = (id) =>
    setDraftQty((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  // Typing is held locally and only committed on blur/Enter - binding the request straight to
  // onChange would fire one POST (and one full server-side recalculation) per keystroke.
  // Every mutation returns the whole recalculated estimate, so totals are never derived in the
  // browser - the server is the only thing that knows the discount/GST/rounding arithmetic.
  const commitBoxQty = async (record) => {
    const newBoxQty = draftQty[record.ID];

    if (newBoxQty === undefined) return;
    if (newBoxQty === null || newBoxQty < 1) {
      clearDraft(record.ID);
      return;
    }
    if (newBoxQty === record.BoxQty) {
      clearDraft(record.ID);
      return;
    }

    setUpdatingId(record.ID);
    try {
      const response = await axios.post(
        global.config.APIURL.BaseURL + "api/Estimate/UpdateEstimateLineQty",
        {
          ID: record.ID,
          BoxQty: newBoxQty,
          QtyInBox: record.QtyInBox,
        },
        { headers: authHeaders() }
      );
      setEstimate(response.data);
      message.success("Quantity updated!");
    } catch (error) {
      message.error(
        error?.response?.data?.Message || "Failed to update quantity."
      );
    }
    // Cleared either way so the field falls back to whatever the server now holds.
    clearDraft(record.ID);
    setUpdatingId(null);
  };

  const handleRemove = async (record) => {
    setUpdatingId(record.ID);
    try {
      const response = await axios.delete(
        global.config.APIURL.BaseURL +
          `api/Estimate/RemoveEstimateLine?ID=${record.ID}`,
        { headers: authHeaders() }
      );
      setEstimate(response.data);
      message.success("Item removed from estimate.");
    } catch (error) {
      message.error(error?.response?.data?.Message || "Failed to remove item.");
    }
    setUpdatingId(null);
  };

  const handleSearch = async (value) => {
    if (!value || !value.trim()) return;
    setSearching(true);
    try {
      const response = await axios.get(
        global.config.APIURL.BaseURL +
          "api/Product/SearchProductByText?SearchText=" +
          encodeURIComponent(value.trim()),
        { headers: authHeaders() }
      );
      setSearchResults(response.data || []);
    } catch (error) {
      message.error("Failed to search products.");
      setSearchResults([]);
    }
    setSearching(false);
  };

  const handleAddProduct = async (product) => {
    setAddingId(product.ProductID);
    try {
      const response = await axios.post(
        global.config.APIURL.BaseURL + "api/Estimate/AddProductToEstimate",
        {
          EstimateID: Number(EstimateID),
          ProductID: product.ProductID,
          BoxQty: 1,
          QtyInBox: product.QtyInBox > 0 ? product.QtyInBox : 1,
          Detail: null,
        },
        { headers: authHeaders() }
      );
      setEstimate(response.data);
      message.success(`${product.DesignNo || product.ProductName} added.`);
      setAddOpen(false);
      setSearchText("");
      setSearchResults([]);
    } catch (error) {
      message.error(error?.response?.data?.Message || "Failed to add product.");
    }
    setAddingId(null);
  };

  // Drives the discount display rule: when the seller applied no discount, every discount-related
  // row and column is hidden. SubTotal equals FinalSubTotal in that case, so what the customer
  // sees still adds up exactly.
  const hasDiscount = Number(estimate?.DiscountAmount) > 0;

  const boxQtyCell = (record) => (
    <InputNumber
      min={1}
      value={draftQty[record.ID] !== undefined ? draftQty[record.ID] : record.BoxQty}
      disabled={!record.IsAvailableOnline || updatingId === record.ID}
      onChange={(value) =>
        setDraftQty((prev) => ({ ...prev, [record.ID]: value }))
      }
      onBlur={() => commitBoxQty(record)}
      onPressEnter={() => commitBoxQty(record)}
      style={{ width: 72 }}
      size="small"
    />
  );

  const removeCell = (record) => (
    <Popconfirm
      title="Remove this item from the estimate?"
      okText="Remove"
      cancelText="Cancel"
      onConfirm={() => handleRemove(record)}
    >
      <Button
        type="text"
        danger
        size="small"
        icon={<DeleteOutlined />}
        loading={updatingId === record.ID}
      />
    </Popconfirm>
  );

  const desktopColumns = [
    {
      title: "Image",
      dataIndex: "ImageUrl",
      key: "ImageUrl",
      render: (url) =>
        url ? (
          <Image
            src={url}
            alt="Product"
            width={60}
            height={60}
            style={{ objectFit: "cover", borderRadius: 8 }}
          />
        ) : (
          <Image src="https://via.placeholder.com/60" width={60} height={60} />
        ),
    },
    {
      title: "Product Name",
      dataIndex: "ProductName",
      key: "ProductName",
      render: (value, record) => (
        <span>
          {value}
          {record.LineSource === "Customer" && (
            <Tag color="green" style={{ marginLeft: 6 }}>
              Added
            </Tag>
          )}
          {!record.IsAvailableOnline && (
            <Tag style={{ marginLeft: 6 }}>Not online</Tag>
          )}
        </span>
      ),
    },
    { title: "Design No", dataIndex: "DesignNo", key: "DesignNo" },
    { title: "Unit", dataIndex: "UnitName", key: "UnitName" },
    {
      title: "Box",
      key: "BoxQty",
      render: (_, record) => boxQtyCell(record),
    },
    { title: "Qty/Box", dataIndex: "QtyInBox", key: "QtyInBox" },
    { title: "Total Qty", dataIndex: "TotalQty", key: "TotalQty" },
    {
      title: "Price",
      dataIndex: "Price",
      key: "Price",
      render: (value) => `₹${formatINR(value)}`,
    },
    {
      title: "Amount",
      dataIndex: "Amount",
      key: "Amount",
      render: (value) => <b>₹{formatINR(value)}</b>,
    },
    // Net Amount only differs from Amount when a discount exists; hidden otherwise.
    ...(hasDiscount
      ? [
          {
            title: "Net Amount",
            dataIndex: "AmountAfterDiscount",
            key: "AmountAfterDiscount",
            render: (value) => <b>₹{formatINR(value)}</b>,
          },
        ]
      : []),
    { title: "Specification", dataIndex: "Detail", key: "Detail" },
    {
      title: "",
      key: "remove",
      render: (_, record) => removeCell(record),
    },
  ];

  const mobileColumns = [
    {
      title: "Product",
      dataIndex: "ID",
      key: "ID",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Image
            src={record.ImageUrl}
            alt={record.ProductName}
            width={50}
            height={50}
            style={{ objectFit: "cover", borderRadius: 6 }}
            fallback="https://via.placeholder.com/50"
          />
          <div style={{ flex: 1 }}>
            <div>
              <b>{record.ProductName}</b>
              {record.LineSource === "Customer" && (
                <Tag color="green" style={{ marginLeft: 6 }}>
                  Added
                </Tag>
              )}
            </div>
            <div>
              <b>Design:</b> {record.DesignNo}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <b>Box:</b> {boxQtyCell(record)}
              <span>
                <b>Qty/Box:</b> {record.QtyInBox}
              </span>
            </div>
            <div>
              <b>Qty:</b> {record.TotalQty}
            </div>
            <div>
              <b>Price:</b> ₹{formatINR(record.Price)}
            </div>
            <div>
              <b>Amount:</b>{" "}
              <span style={{ color: "#1890ff" }}>
                ₹{formatINR(hasDiscount ? record.AmountAfterDiscount : record.Amount)}
              </span>
            </div>
            {record.Detail && (
              <div>
                <b>Spec:</b> {record.Detail}
              </div>
            )}
            <div style={{ marginTop: 4 }}>{removeCell(record)}</div>
          </div>
        </div>
      ),
    },
  ];

  const columns = isMobile ? mobileColumns : desktopColumns;

  const SummaryRow = ({ label, value, strong }) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "3px 0",
      }}
    >
      <Text strong={strong}>{label}</Text>
      <Text strong={strong} style={strong ? { color: "#1890ff" } : undefined}>
        ₹{formatINR(value)}
      </Text>
    </div>
  );

  return (
    <div style={{ padding: 0, margin: 0 }}>
      <Card style={{ padding: 0, margin: 0 }}>
        <div
          style={{
            maxWidth: 1100,
            margin: isMobile ? 0 : "auto",
            padding: isMobile ? 8 : 16,
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <Button
              type={isMobile ? "default" : "primary"}
              icon={<CaretLeftOutlined />}
              onClick={() => navigate("/myestimates")}
              size={isMobile ? "small" : "middle"}
            >
              Back to Estimate List
            </Button>
          </div>
          <Title
            level={2}
            style={{
              marginBottom: 16,
              fontSize: isMobile ? 20 : 28,
              textAlign: isMobile ? "center" : "left",
            }}
          >
            Estimate Detail
          </Title>

          {loading ? (
            <div style={{ textAlign: "center", marginTop: 40 }}>
              <Spin size="large" />
            </div>
          ) : estimate ? (
            <>
              <Card style={{ marginBottom: 24 }}>
                <Row gutter={[16, 8]}>
                  <Col xs={24} md={12}>
                    <Text strong>Estimate No: </Text>
                    <Text>{estimate.FinalEstimateNo}</Text>
                    <br />
                    <Text strong>Date: </Text>
                    <Text>{formatDate(estimate.EstimateDate)}</Text>
                    <br />
                    <Text strong>Total Box: </Text>
                    <Text>{estimate.TotalBox}</Text>
                    <br />
                    <Text strong>Total Quantity: </Text>
                    <Text>{estimate.TotalQuantity}</Text>
                  </Col>
                  <Col xs={24} md={12}>
                    {/* Discount rows appear only when a discount was actually applied. When it is
                        zero SubTotal === FinalSubTotal, so dropping them leaves no gap. */}
                    <SummaryRow label="Sub Total" value={estimate.SubTotal} />
                    {hasDiscount && (
                      <>
                        <SummaryRow
                          label={`Discount (${estimate.DiscountPer}%)`}
                          value={estimate.DiscountAmount}
                        />
                        <SummaryRow
                          label="Final Sub Total"
                          value={estimate.FinalSubTotal}
                        />
                      </>
                    )}
                    <SummaryRow label="CGST" value={estimate.TotalCgst} />
                    <SummaryRow label="SGST" value={estimate.TotalSgst} />
                    <SummaryRow label="Round Off" value={estimate.RoundOff} />
                    <SummaryRow
                      label="Grand Total"
                      value={estimate.GrandTotal}
                      strong
                    />
                  </Col>
                </Row>
                {(estimate.Narration || estimate.Note) && (
                  <>
                    <Divider style={{ margin: "12px 0" }} />
                    {estimate.Narration && (
                      <div>
                        <Text strong>Narration: </Text>
                        <Text>{estimate.Narration}</Text>
                      </div>
                    )}
                    {estimate.Note && (
                      <div>
                        <Text strong>Note: </Text>
                        <Text>{estimate.Note}</Text>
                      </div>
                    )}
                  </>
                )}
              </Card>

              <Divider orientation="center">Estimate Items</Divider>

              <div style={{ marginBottom: 12, textAlign: "right" }}>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => setAddOpen(true)}
                  size={isMobile ? "small" : "middle"}
                >
                  Add Product
                </Button>
              </div>

              <Table
                columns={columns}
                dataSource={estimate.EstimateDetail}
                rowKey={(record) => record.ID}
                pagination={false}
                bordered
                size={isMobile ? "small" : "middle"}
                scroll={isMobile ? { x: true } : undefined}
              />

              <Modal
                open={addOpen}
                title="Add Product to Estimate"
                onCancel={() => setAddOpen(false)}
                footer={null}
                width={isMobile ? "95%" : 640}
              >
                <Input.Search
                  placeholder="Search by design no or product name"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={handleSearch}
                  enterButton
                  loading={searching}
                  allowClear
                />
                <div style={{ marginTop: 16, maxHeight: 380, overflowY: "auto" }}>
                  {searching ? (
                    <div style={{ textAlign: "center", padding: 24 }}>
                      <Spin />
                    </div>
                  ) : searchResults.length === 0 ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="Search for a product to add"
                    />
                  ) : (
                    <List
                      dataSource={searchResults}
                      rowKey={(item) => item.ProductID}
                      renderItem={(item) => (
                        <List.Item
                          actions={[
                            <Button
                              type="primary"
                              size="small"
                              loading={addingId === item.ProductID}
                              onClick={() => handleAddProduct(item)}
                            >
                              Add
                            </Button>,
                          ]}
                        >
                          <List.Item.Meta
                            avatar={
                              <Image
                                src={item.ImageUrl}
                                width={48}
                                height={48}
                                style={{ objectFit: "cover", borderRadius: 6 }}
                                fallback="https://via.placeholder.com/48"
                              />
                            }
                            title={item.ProductName}
                            description={
                              <Space size="middle" wrap>
                                <span>Design: {item.DesignNo}</span>
                                <span>
                                  ₹
                                  {formatINR(
                                    item.DealRate > 0
                                      ? item.DealRate
                                      : item.SalesRate
                                  )}
                                </span>
                                <span>Qty/Box: {item.QtyInBox}</span>
                              </Space>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  )}
                </div>
              </Modal>
            </>
          ) : (
            <div style={{ textAlign: "center", marginTop: 40 }}>
              <Text type="danger">Estimate not found.</Text>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default EstimateDetail;
